/**
 * Hash routes (UX-LAY-02): `#/overview`, `#/read?p=<paragraph>&ch=<ids>`, `#/channels`, `#/channel/<ID>`,
 * `#/hotspots?…filters`, `#/diagnostics`. Routes hold positions and filters, never text.
 */
import { signal } from '@preact/signals';

export type View =
	| 'overview'
	| 'read'
	| 'channels'
	| 'channel'
	| 'hotspots'
	| 'diagnostics'
	| 'bench'
	| 'glossary';

export interface Route {
	view: View;
	/** The channel ID of `#/channel/<ID>`. */
	id?: string;
	params: URLSearchParams;
	/** A fragment after the route (`#/glossary#kappa`): the element to scroll to. */
	anchor?: string;
}

const VIEWS: readonly View[] = [
	'overview',
	'read',
	'channels',
	'channel',
	'hotspots',
	'diagnostics',
	'bench',
	'glossary',
];

function decodeAnchor(a: string): string | undefined {
	try {
		return a ? decodeURIComponent(a) : undefined;
	} catch {
		return a || undefined;
	}
}

export function parseHash(hash: string): Route {
	let raw = hash.replace(/^#\/?/, '');
	// a second `#` starts an in-page anchor (`#/glossary#kappa`); query values are percent-encoded, so none hides there
	const hashAt = raw.indexOf('#');
	const anchor = hashAt >= 0 ? decodeAnchor(raw.slice(hashAt + 1)) : undefined;
	if (hashAt >= 0) raw = raw.slice(0, hashAt);
	const q = raw.indexOf('?');
	const path = q >= 0 ? raw.slice(0, q) : raw;
	const params = new URLSearchParams(q >= 0 ? raw.slice(q + 1) : '');
	const [first, second] = path.split('/');
	const view = VIEWS.find((v) => v === first) ?? 'overview';
	const route: Route = { view, params };
	if (anchor) route.anchor = anchor;
	if (view === 'channel' && second) route.id = decodeURIComponent(second);
	return route;
}

export function hashFor(
	view: View,
	opts: { id?: string; params?: Record<string, string | undefined>; anchor?: string } = {}
): string {
	let h = `#/${view}`;
	if (view === 'channel' && opts.id) h += `/${encodeURIComponent(opts.id)}`;
	const p = new URLSearchParams();
	for (const [k, v] of Object.entries(opts.params ?? {}))
		if (v !== undefined && v !== '') p.set(k, v);
	const s = p.toString();
	const base = s ? `${h}?${s}` : h;
	return opts.anchor ? `${base}#${encodeURIComponent(opts.anchor)}` : base;
}

export const route = signal<Route>(parseHash(typeof location === 'undefined' ? '' : location.hash));

if (typeof window !== 'undefined') {
	window.addEventListener('hashchange', () => {
		route.value = parseHash(location.hash);
	});
}

export function navigate(hash: string, opts: { replace?: boolean } = {}): void {
	if (opts.replace) history.replaceState(null, '', hash);
	else if (location.hash !== hash) location.hash = hash;
	route.value = parseHash(hash);
}

/** Update parameters of the current route in place (scroll position, filters) without adding history entries. */
export function setParams(params: Record<string, string | undefined>): void {
	const r = route.value;
	navigate(hashFor(r.view, { id: r.id, params: { ...Object.fromEntries(r.params), ...params } }), {
		replace: true,
	});
}
